import { InputAdornment, ListSubheader } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Spacing } from '../../design-system/spacing';
import { PasswordStrengthOptions } from '../../utils/zxcvbn';
import { FormError } from '../FormError';
import { IconButton } from '../IconButton';
import { CloseIcon } from '../Icons/CloseIcon';
import { DollarSignIcon } from '../Icons/DollarSignIcon';
import { ExpandMoreIcon } from '../Icons/ExpandMoreIcon';
import { HelpOutlineIcon } from '../Icons/HelpOutlineIcon';
import { VisibilityIcon } from '../Icons/VisibilityIcon';
import { VisibilityOffIcon } from '../Icons/VisibilityOffIcon';
import { PasswordStrengthThermometer } from '../PasswordStrengthThermometer';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { CurrencyField } from './CurrencyField';
import {
  MainColorIcon,
  StyledBoxWrapper,
  StyledChipWrapperBox,
  StyledDatePickerField,
  StyledField,
  StyledIconWrapper,
  StyledListSubheaderBox,
  StyledListSubheaderContent,
  StyledMenuItem,
  StyledMenuItemContent,
  StyledSelectedOptionBox,
  StyledTooltipBox,
} from './Field.styles';
import { PhoneNumberField } from './PhoneNumberField';
import { FieldProps, FieldTypes, MenuItemType } from './types';
import { getMenuItem } from './utils';

export function Field({
  type,
  kind = 'light',
  menuItems,
  iconLeft,
  mb,
  mt,
  ml,
  mr,
  pt,
  pb,
  pl,
  pr,
  margin,
  padding,
  width,
  errorMsg,
  showError = true,
  showPasswordThermometer = false,
  passwordStrength = PasswordStrengthOptions.Weakest,
  showRequirementTooltip = false,
  inputProps = {},
  handleDatePickerChange,
  clearable,
  showDescription = false,
  menuOptionsMaxHeight = 320,
  ...rest
}: FieldProps) {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = useCallback(
    () => setShowPassword((prev) => !prev),
    [],
  );

  const commonProps = useMemo(
    () => ({
      error: Boolean(errorMsg),
      fullWidth: true,
      helperText: showError ? (
        <FormError
          aria-live="polite"
          errorText={errorMsg}
          showError={showError}
        />
      ) : null,
      kind,
      sx: {
        marginBottom: mb,
        marginTop: mt,
        marginLeft: ml,
        marginRight: mr,
        paddingTop: pt,
        paddingBottom: pb,
        paddingLeft: pl,
        paddingRight: pr,
        margin,
        padding,
        width,
      },
      ...rest,
    }),
    [
      errorMsg,
      showError,
      kind,
      mb,
      mt,
      ml,
      mr,
      pt,
      pb,
      pl,
      pr,
      margin,
      padding,
      width,
      rest,
    ],
  );

  const PasswordField = useMemo(
    () => (
      <>
        <StyledField
          {...commonProps}
          id={`password-${rest.label}`}
          label={rest.label}
          margin="normal"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <MainColorIcon
                    aria-label="Toggle visibility"
                    edge="end"
                    onClick={handleClickShowPassword}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </MainColorIcon>
                </InputAdornment>
              ),
              ...inputProps,
            },
          }}
          type={showPassword ? 'text' : 'password'}
        />
        {showPasswordThermometer ? (
          <PasswordStrengthThermometer
            showRequirementTooltip={showRequirementTooltip}
            strength={passwordStrength}
          />
        ) : null}
      </>
    ),
    [
      commonProps,
      handleClickShowPassword,
      passwordStrength,
      rest.label,
      showPassword,
      showPasswordThermometer,
      showRequirementTooltip,
      inputProps,
    ],
  );

  const TextField = useMemo(
    () => (
      <StyledField
        {...commonProps}
        slotProps={{
          input: {
            startAdornment: iconLeft && (
              <StyledIconWrapper>{iconLeft}</StyledIconWrapper>
            ),
            endAdornment: clearable && (
              <IconButton
                aria-label="clear input"
                onClick={() =>
                  rest.onChange({
                    target: { value: '' },
                  } as ChangeEvent<HTMLInputElement>)
                }
              >
                <CloseIcon />
              </IconButton>
            ),
            ...inputProps,
          },
        }}
      />
    ),
    [clearable, commonProps, iconLeft, inputProps, rest],
  );

  const CurrencyComponentField = useMemo(
    () => (
      <CurrencyField
        {...commonProps}
        slotProps={{
          input: {
            startAdornment: (
              <StyledIconWrapper>
                {iconLeft || <DollarSignIcon />}
              </StyledIconWrapper>
            ),
            ...inputProps,
          },
        }}
      />
    ),
    [commonProps, iconLeft, inputProps],
  );

  const SelectField = useMemo(
    () => (
      <StyledField
        {...commonProps}
        select
        slotProps={{
          select: {
            renderValue: (selected) => {
              const selectedItem = menuItems?.find(
                (item) => item.key === selected,
              );
              if (!selectedItem) return '';
              return (
                <StyledMenuItemContent>
                  <StyledBoxWrapper>
                    {selectedItem.leftIcon ? (
                      <StyledIconWrapper side="left">
                        {selectedItem.leftIcon}
                      </StyledIconWrapper>
                    ) : null}
                    <StyledSelectedOptionBox>
                      {getMenuItem(selectedItem, showDescription)}
                    </StyledSelectedOptionBox>
                  </StyledBoxWrapper>
                  {selectedItem.rightChip ? (
                    <StyledChipWrapperBox>
                      <StyledIconWrapper side="right">
                        {selectedItem.rightChip}
                      </StyledIconWrapper>
                    </StyledChipWrapperBox>
                  ) : null}
                </StyledMenuItemContent>
              );
            },
            IconComponent: ExpandMoreIcon,
            MenuProps: {
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: {
                sx: {
                  maxHeight: menuOptionsMaxHeight,
                  width: 100,
                  mt: Spacing.Tiny,
                },
              },
            },
          },
          input: {
            ...inputProps,
          },
        }}
      >
        {menuItems?.map((menuItem) => {
          const {
            key,
            leftIcon,
            rightChip,
            itemType = MenuItemType.option,
            groupLabel,
            groupTooltip,
            tooltipMessage,
            tooltipIcon,
            groupChip,
          } = menuItem;
          return itemType === MenuItemType.option ? (
            <StyledMenuItem value={key}>
              <StyledBoxWrapper>
                {leftIcon ? (
                  <StyledIconWrapper side="left">{leftIcon}</StyledIconWrapper>
                ) : null}
                <StyledSelectedOptionBox>
                  {getMenuItem(menuItem, showDescription)}
                </StyledSelectedOptionBox>
              </StyledBoxWrapper>
              <StyledChipWrapperBox>
                {rightChip ? (
                  <StyledIconWrapper side="right">
                    {rightChip}
                  </StyledIconWrapper>
                ) : null}
              </StyledChipWrapperBox>
            </StyledMenuItem>
          ) : (
            <ListSubheader disableGutters>
              <StyledListSubheaderContent>
                <StyledListSubheaderBox>
                  <Typography variant="subtitle">{groupLabel}</Typography>
                  {groupChip ? groupChip : null}
                </StyledListSubheaderBox>
                <StyledTooltipBox>
                  {groupTooltip ? (
                    <Tooltip placement="bottom-end" title={tooltipMessage}>
                      <IconButton aria-label="Tooltip button">
                        {tooltipIcon || <HelpOutlineIcon />}
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </StyledTooltipBox>
              </StyledListSubheaderContent>
            </ListSubheader>
          );
        })}
      </StyledField>
    ),
    [commonProps, inputProps, menuItems, menuOptionsMaxHeight, showDescription],
  );

  const PhoneField = useMemo(
    () => <PhoneNumberField {...commonProps} />,
    [commonProps],
  );

  const DateField = useMemo(
    () => (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <StyledDatePickerField
          disabled={rest.disabled}
          error={Boolean(errorMsg)}
          kind={kind}
          label={rest.label}
          maxDate={rest.maxDate ? dayjs(rest.maxDate) : undefined}
          minDate={rest.minDate ? dayjs(rest.minDate) : undefined}
          onChange={handleDatePickerChange}
          slotProps={{
            textField: {
              error: Boolean(errorMsg),
            },
          }}
          value={rest.value ? dayjs(rest.value) : null}
        />
        <FormError
          aria-live="polite"
          errorText={errorMsg ? errorMsg : undefined}
          showError={showError}
        />
      </LocalizationProvider>
    ),
    [
      errorMsg,
      handleDatePickerChange,
      kind,
      rest.disabled,
      rest.label,
      rest.maxDate,
      rest.minDate,
      rest.value,
      showError,
    ],
  );

  switch (type) {
    case FieldTypes.password:
      return PasswordField;
    case FieldTypes.text:
      return TextField;
    case FieldTypes.currency:
      return CurrencyComponentField;
    case FieldTypes.select:
      return SelectField;
    case FieldTypes.phone:
      return PhoneField;
    case FieldTypes.date:
      return DateField;
    default:
      throw new Error('Invalid prop value for `type`.');
  }
}
